import { memo, useMemo } from 'react'

import { useBackup, useContactData } from 'clickNgo/features/contact/contactDeactivation/contexts'
import { ReassignedItem, ReassignmentItem } from 'clickNgo/features/contact/contactReassignment'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'

import { useReassignment } from '../../../contexts/ReassignmentContext'
import { useContactRelink } from '../../../hooks'
import { ContactRelinkingStepProps } from '../../../models'
import { contactRelinkingModalTranslations } from '../../../translation'
import { ContactRelinkingFooter } from '../../contactRelinkingFooter'
import { ContactRelinkingHeader } from '../../contactRelinkingHeader'

import styles from './CompletionStep.module.scss'

type Props = Pick<ContactRelinkingStepProps, 'onCancel'> & {
  onRelink: (newHpRoles?: string[]) => void
}

export const CompletionStep = memo<Props>(({ onCancel, onRelink }) => {
  const { contact } = useContactData()
  const { rolesToReassign, flagsToReassign, uniqueRolesToReassign, availableContacts } =
    useReassignment()
  const { backupedContact } = useBackup()

  const { relinkContact, loaderState } = useContactRelink({
    contact,
    rolesToReassign,
    flagsToReassign,
    onRelink,
  })

  const reassignedRoles: ReassignmentItem[] = useMemo(
    () =>
      rolesToReassign.map((role) => ({
        key: role.role,
        displayName: role.role,
        contact: role.contact,
      })),
    [rolesToReassign]
  )

  const reassignedUniqueRoles: ReassignmentItem[] = useMemo(
    () =>
      uniqueRolesToReassign.map((role) => ({
        key: role.role,
        displayName: role.role,
        contact: role.contact,
      })),
    [uniqueRolesToReassign]
  )

  const reassignedFlags: ReassignmentItem[] = useMemo(
    () =>
      flagsToReassign.map((flag) => ({
        key: flag.flag.code,
        displayName: flag.flag.code,
        contact: flag.contact,
      })),
    [flagsToReassign]
  )

  return (
    <div className={styles.container}>
      <LoaderOverlay isVisible={loaderState.isLoading} message={loaderState.message} />
      <ContactRelinkingHeader
        title={contactRelinkingModalTranslations.completionStep.title}
        description={contactRelinkingModalTranslations.completionStep.description}
        descriptionParams={[{ key: '{email}', value: contact.email }]}
      />
      <div className={styles.content}>
        <RenderWhen condition={availableContacts.length > 0 && uniqueRolesToReassign.length > 0}>
          <ReassignedItem
            title={contactRelinkingModalTranslations.completionStep.reassignedUniqueRoles}
            items={reassignedUniqueRoles}
          />
        </RenderWhen>
        <RenderWhen condition={availableContacts.length > 0 && rolesToReassign.length > 0}>
          <ReassignedItem
            title={contactRelinkingModalTranslations.completionStep.reassignedRoles}
            items={reassignedRoles}
          />
        </RenderWhen>

        <RenderWhen condition={availableContacts.length > 0 && flagsToReassign.length > 0}>
          <ReassignedItem
            title={contactRelinkingModalTranslations.completionStep.reassignedFlags}
            items={reassignedFlags}
          />
        </RenderWhen>
        <RenderWhen condition={!!backupedContact}>
          <div className={styles.block}>
            <h6>
              <Translation
                text={contactRelinkingModalTranslations.completionStep.backupTitle}
                span
              />
            </h6>
            <p>
              <Translation
                text={contactRelinkingModalTranslations.completionStep.backupDescription}
                replaceParams={[{ key: '{email}', value: backupedContact?.email || '' }]}
                span
              />
            </p>
          </div>
        </RenderWhen>
      </div>
      <ContactRelinkingFooter onCancel={onCancel} onProceed={relinkContact} />
    </div>
  )
})
